import type { ElementType } from 'react';

import type { BasePanelProps } from './types';
import { resolveBaseAppearance } from './theme';
import { cn, hasRenderableNode } from './utils';

export function BasePanel({
  as = 'article',
  title,
  description,
  media,
  header,
  footer,
  children,
  interactive,
  selected,
  disabled,
  appearance,
  variant,
  density,
  state,
  className,
  classNames,
  style,
  ...panelProps
}: BasePanelProps) {
  const theme = resolveBaseAppearance({ ...appearance, variant, density, state });
  const Component = as as ElementType;
  const hasHeader =
    header !== undefined
      ? hasRenderableNode(header)
      : hasRenderableNode(title) || hasRenderableNode(description);
  const hasMedia = media !== undefined && media !== null;
  const bodyIsStandalone = !hasHeader && !hasMedia;

  return (
    <Component
      {...panelProps}
      aria-disabled={disabled || undefined}
      data-variant={theme.variant}
      data-density={theme.density}
      data-state={state ?? (disabled ? 'disabled' : selected ? 'selected' : 'idle')}
      data-monkeys-component="panel"
      data-slot="root"
      data-interactive={interactive || undefined}
      data-selected={selected || undefined}
      data-disabled={disabled || undefined}
      className={cn(
        theme.slots.panelRoot,
        interactive && !disabled && theme.slots.panelInteractive,
        selected && theme.slots.panelSelected,
        disabled && theme.slots.panelDisabled,
        classNames?.root,
        className,
      )}
      style={{
        ...theme.styles.panel,
        ...style,
      }}
    >
      {hasMedia ? (
        <div
          data-slot="media"
          className={cn(theme.slots.panelMedia, classNames?.media)}
        >
          {media}
        </div>
      ) : null}

      {hasHeader ? (
        <div
          data-slot="header"
          className={cn(theme.slots.panelHeader, classNames?.header)}
          style={theme.styles.panelHeader}
        >
          {header !== undefined ? (
            header
          ) : (
            <>
              {hasRenderableNode(title) ? (
                <div
                  data-slot="title"
                  className={cn(theme.slots.panelTitle, classNames?.title)}
                >
                  {title}
                </div>
              ) : null}
              {hasRenderableNode(description) ? (
                <div
                  data-slot="description"
                  className={cn(
                    theme.slots.panelDescription,
                    classNames?.description,
                  )}
                >
                  {description}
                </div>
              ) : null}
            </>
          )}
        </div>
      ) : null}

      {children !== undefined && children !== null ? (
        <div
          data-slot="body"
          className={cn(
            theme.slots.panelBody,
            bodyIsStandalone && theme.slots.panelBodyStandalone,
            classNames?.body,
          )}
          style={theme.styles.panelBody}
        >
          {children}
        </div>
      ) : null}

      {footer !== undefined && footer !== null ? (
        <div
          data-slot="footer"
          className={cn(theme.slots.panelFooter, classNames?.footer)}
          style={theme.styles.panelFooter}
        >
          {footer}
        </div>
      ) : null}
    </Component>
  );
}
